import { useEffect, useMemo, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Power, GripVertical } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

const empty = { name: "", name_es: "", category: "", parent_category: "", duration_minutes: 45, price: 0, price_is_minimum: false, active: true };

// Default suggestions shown when DB has no parent categories yet (first run).
// Owners can still type any new category name they want — these are just hints.
const DEFAULT_PARENT_SUGGESTIONS = [
  "Pedicure", "Manicure", "Acrylic", "Dipping",
  "Gel-X", "Waxing", "Kids", "Add-on", "Other",
];

export default function ManageServices() {
  const [services, setServices] = useState([]);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [svcRes, catRes] = await Promise.all([
      api.get("/public/services"),
      api.get("/admin/category-order").catch(() => ({ data: { order: [] } })),
    ]);
    setServices(svcRes.data);
    setCategoryOrder(catRes.data.order || []);
  };
  useEffect(() => { load(); }, []);

  // Collect ALL existing parent categories from the DB so the editor can offer
  // them as one-click pills (plus a free-text field for brand-new categories).
  const existingParents = useMemo(() => {
    const set = new Set();
    services.forEach((s) => {
      const p = (s.parent_category || s.category || "").trim();
      if (p) set.add(p);
    });
    // Merge with defaults so first-run owners still see useful suggestions
    DEFAULT_PARENT_SUGGESTIONS.forEach((d) => set.add(d));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [services]);

  const save = async (form) => {
    try { if (editing) await api.patch(`/admin/services/${editing.id}`, form); else await api.post("/admin/services", form); toast.success(editing ? "Updated" : "Created"); setOpen(false); load(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const remove = async (s) => { if (!window.confirm(`Deactivate ${s.name}?`)) return; try { await api.delete(`/admin/services/${s.id}`); toast.success("Deactivated"); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); } };

  // Group services by parent_category, honoring saved category order.
  // Categories not in the saved order get appended alphabetically.
  const grouped = useMemo(() => {
    const map = services.reduce((acc, s) => {
      const c = s.parent_category || s.category || "Other";
      (acc[c] ||= []).push(s);
      return acc;
    }, {});
    // Sort services within each group by sort_order then name
    Object.values(map).forEach((arr) => arr.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name)));
    return map;
  }, [services]);

  const orderedCategories = useMemo(() => {
    const known = new Set(Object.keys(grouped));
    const fromSaved = categoryOrder.filter((c) => known.has(c));
    const remaining = Array.from(known).filter((c) => !fromSaved.includes(c)).sort();
    return [...fromSaved, ...remaining];
  }, [grouped, categoryOrder]);

  const moveCategory = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const next = [...orderedCategories];
    const [m] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, m);
    setCategoryOrder(next);
    try {
      await api.post("/admin/category-order", { order: next });
      toast.success("Category order saved");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); load(); }
  };

  const moveServiceInCategory = async (cat, fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const list = [...(grouped[cat] || [])];
    const [m] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, m);
    // Optimistic local update so UI moves immediately
    const newOrders = list.map((s, i) => ({ ...s, sort_order: i }));
    const updated = services.map((s) => {
      const hit = newOrders.find((x) => x.id === s.id);
      return hit ? hit : s;
    });
    setServices(updated);
    try {
      await api.post("/admin/services/reorder",
        newOrders.map((s) => ({ id: s.id, sort_order: s.sort_order })));
      toast.success("Service order saved");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); load(); }
  };

  // Drag state: { type:'cat'|'svc', cat, fromIdx }
  const dragRef = useRef(null);

  return (
    <AdminLayout title="Services" showTurnBoard={false}>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div className="text-xs text-zen-muted italic" data-testid="reorder-hint">
          Drag the <GripVertical className="w-3 h-3 inline -mt-0.5" /> handle to reorder categories OR services within a category.
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-zen-primary text-white" data-testid="new-service-btn">
          <Plus className="w-4 h-4 mr-1" /> New Service
        </Button>
      </div>
      {orderedCategories.map((cat, catIdx) => (
        <div
          key={cat}
          className="mb-8"
          data-testid={`category-group-${cat}`}
          onDragOver={(e) => {
            if (dragRef.current?.type === "cat") { e.preventDefault(); }
          }}
          onDrop={(e) => {
            if (dragRef.current?.type === "cat") {
              e.preventDefault();
              moveCategory(dragRef.current.fromIdx, catIdx);
              dragRef.current = null;
            }
          }}>
          <div
            className="flex items-center gap-2 mb-3 cursor-grab select-none"
            draggable
            onDragStart={() => { dragRef.current = { type: "cat", fromIdx: catIdx }; }}
            onDragEnd={() => { dragRef.current = null; }}
            data-testid={`category-header-${cat}`}>
            <GripVertical className="w-4 h-4 text-zen-muted" />
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zen-ink">{cat}</div>
            <span className="text-[10px] text-zen-muted">({grouped[cat].length})</span>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {grouped[cat].map((s, svcIdx) => (
              <div
                key={s.id}
                className="bg-white rounded-2xl border border-zen-border p-5 flex items-start gap-2"
                draggable
                onDragStart={(e) => {
                  e.stopPropagation();
                  dragRef.current = { type: "svc", cat, fromIdx: svcIdx };
                }}
                onDragOver={(e) => {
                  if (dragRef.current?.type === "svc" && dragRef.current.cat === cat) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onDrop={(e) => {
                  if (dragRef.current?.type === "svc" && dragRef.current.cat === cat) {
                    e.preventDefault();
                    e.stopPropagation();
                    moveServiceInCategory(cat, dragRef.current.fromIdx, svcIdx);
                    dragRef.current = null;
                  }
                }}
                onDragEnd={() => { dragRef.current = null; }}
                data-testid={`service-card-${s.id}`}>
                <GripVertical className="w-4 h-4 text-zen-muted mt-1 flex-shrink-0 cursor-grab" />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-serif text-xl text-zen-ink">{s.name}</div>
                      <div className="text-sm text-zen-muted mt-3">Duration: {s.duration_minutes} min · Price: ${s.price}{s.price_is_minimum ? "+" : ""} · {s.active ? "Active" : "Inactive"}</div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(s); setOpen(true); }} data-testid={`edit-service-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(s)} data-testid={`delete-service-${s.id}`}><Power className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <ServiceDialog open={open} onClose={() => setOpen(false)} editing={editing} onSave={save} existingParents={existingParents} />
    </AdminLayout>
  );
}

function ServiceDialog({ open, onClose, editing, onSave, existingParents }) {
  const [form, setForm] = useState(empty);
  useEffect(() => { setForm(editing ? { ...empty, ...editing } : empty); }, [editing, open]);
  const currentParent = (form.parent_category || "").trim();
  const filtered = currentParent
    ? existingParents.filter((p) => p.toLowerCase().includes(currentParent.toLowerCase()) && p.toLowerCase() !== currentParent.toLowerCase())
    : existingParents;
  const isNew = currentParent && !existingParents.some((p) => p.toLowerCase() === currentParent.toLowerCase());
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="service-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit Service" : "New Service"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zen-surface border-0" data-testid="svc-name" />
          <Input placeholder="Spanish Name" value={form.name_es} onChange={(e) => setForm({ ...form, name_es: e.target.value })} className="bg-zen-surface border-0" data-testid="svc-name-es" />

          {/* Parent Category — free text input + clickable pills below.
              Type anything (even brand-new categories) OR tap an existing pill. */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold uppercase tracking-wider text-zen-ink">Parent Category</label>
              {isNew && (
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full" data-testid="svc-parent-new-tag">
                  + NEW CATEGORY
                </span>
              )}
            </div>
            <Input
              placeholder="Type a category (e.g. Pedicure) or pick one below"
              value={form.parent_category || ""}
              onChange={(e) => setForm({ ...form, parent_category: e.target.value })}
              className="bg-zen-surface border-0"
              data-testid="svc-parent-category-input"
            />
            {filtered.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="svc-parent-suggestions">
                {filtered.slice(0, 12).map((c) => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => setForm({ ...form, parent_category: c })}
                    className={`text-xs px-2.5 py-1 rounded-full border transition ${currentParent.toLowerCase() === c.toLowerCase()
                      ? "bg-zen-primary text-white border-zen-primary"
                      : "bg-white border-zen-border text-zen-ink hover:border-zen-primary hover:bg-zen-surface"}`}
                    data-testid={`svc-parent-pill-${c}`}>
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Input placeholder="Sub-category / tag (optional)" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-zen-surface border-0" data-testid="svc-category" />
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" placeholder="Duration (min)" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value || 0) })} className="bg-zen-surface border-0" data-testid="svc-duration" />
            <Input type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value || 0) })} className="bg-zen-surface border-0" data-testid="svc-price" />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-zen-surface">
            <div>
              <div className="text-sm font-medium">Price is minimum ("and up")</div>
              <div className="text-xs text-zen-muted mt-0.5">Show as "${form.price || 0}+" — for designs without fixed pricing</div>
            </div>
            <Switch checked={!!form.price_is_minimum} onCheckedChange={(v) => setForm({ ...form, price_is_minimum: v })} data-testid="svc-price-minimum" />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-zen-surface">
            <span className="text-sm">Active</span>
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="svc-active" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(form)} className="bg-zen-primary text-white" data-testid="svc-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
